/**
 * Workshop Preview Page
 * Full-page preview that simulates participant experience
 */

import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useWorkshop } from "@hooks/useWorkshops";
import { useWorkshopExercises } from "@hooks/useExercises";
import { MarkdownRenderer } from "@components/exercise";
import { PresentationViewer } from "@components/content";
import { Button } from "@components/common";
import { StatusBadge } from "@components/admin";
import type { Exercise } from "@/types";

// Icons
const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const EyeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const PresentationIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const CodeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const InteractiveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
    />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

function getContentTypeIcon(contentType: string) {
  switch (contentType) {
    case "presentation":
      return <PresentationIcon className="w-4 h-4" />;
    case "jupyter":
      return <CodeIcon className="w-4 h-4" />;
    case "interactive":
      return <InteractiveIcon className="w-4 h-4" />;
    default:
      return <DocumentIcon className="w-4 h-4" />;
  }
}

function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return "";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// Status badge variant mapping
const statusVariants: Record<string, "success" | "warning" | "neutral"> = {
  active: "success",
  draft: "warning",
  completed: "success",
  archived: "neutral",
};

const difficultyVariants: Record<string, "success" | "warning" | "info"> = {
  beginner: "success",
  intermediate: "warning",
  advanced: "info",
};

export function WorkshopPreviewPage() {
  const { workshopId } = useParams<{ workshopId: string }>();
  const navigate = useNavigate();
  const [selectedExerciseIndex, setSelectedExerciseIndex] = useState(0);

  const { data: workshop, isLoading: workshopLoading } =
    useWorkshop(workshopId);
  const { data: exercises = [], isLoading: exercisesLoading } =
    useWorkshopExercises(workshopId);

  // Sort exercises by order_index
  const sortedExercises = useMemo(() => {
    return [...exercises].sort((a, b) => a.order_index - b.order_index);
  }, [exercises]);

  const selectedExercise = sortedExercises[selectedExerciseIndex];

  const goToPrevExercise = () => {
    if (selectedExerciseIndex > 0) {
      setSelectedExerciseIndex(selectedExerciseIndex - 1);
    }
  };

  const goToNextExercise = () => {
    if (selectedExerciseIndex < sortedExercises.length - 1) {
      setSelectedExerciseIndex(selectedExerciseIndex + 1);
    }
  };

  // Render exercise content based on type
  const renderExerciseContent = (exercise: Exercise) => {
    switch (exercise.content_type) {
      case "presentation":
        return (
          <PresentationViewer exerciseId={exercise.id} title={exercise.title} />
        );

      case "markdown":
        if (exercise.content_markdown) {
          return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
              <MarkdownRenderer content={exercise.content_markdown} />
            </div>
          );
        }
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <DocumentIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No markdown content available for this exercise.</p>
            {exercise.content_path && (
              <p className="mt-2 text-sm font-mono">{exercise.content_path}</p>
            )}
          </div>
        );

      case "jupyter":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <CodeIcon className="w-12 h-12 mx-auto mb-4 text-gray-400 opacity-50" />
            <p className="font-medium mb-2">Jupyter Notebook</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Jupyter notebooks are displayed in a specialized viewer when
              accessed by participants.
            </p>
            {exercise.content_path && (
              <p className="mt-4 text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg inline-block">
                {exercise.content_path}
              </p>
            )}
          </div>
        );

      case "interactive":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <InteractiveIcon className="w-12 h-12 mx-auto mb-4 text-gray-400 opacity-50" />
            <p className="font-medium mb-2">Interactive Exercise</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Interactive exercises provide hands-on learning experiences.
            </p>
            {exercise.content_path && (
              <p className="mt-4 text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg inline-block">
                {exercise.content_path}
              </p>
            )}
          </div>
        );

      default:
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <DocumentIcon className="w-12 h-12 mx-auto mb-4 text-gray-400 opacity-50" />
            <p>Preview not available for this content type.</p>
          </div>
        );
    }
  };

  // Loading state
  if (workshopLoading || exercisesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Loading workshop preview...
          </p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!workshop) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Workshop Not Found
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            The requested workshop could not be found.
          </p>
          <Button onClick={() => navigate("/admin")}>Back to Admin</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Preview Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EyeIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Preview Mode - This is how participants will see the workshop
            </span>
            <StatusBadge
              label={
                workshop.status.charAt(0).toUpperCase() +
                workshop.status.slice(1)
              }
              variant={statusVariants[workshop.status] || "neutral"}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            className="text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Exit Preview
          </Button>
        </div>
      </div>

      {/* Workshop Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                <Link
                  to="/workshops"
                  className="hover:text-primary-600 dark:hover:text-primary-400"
                >
                  Workshops
                </Link>
                <span>/</span>
                <span className="text-gray-900 dark:text-white">
                  {workshop.title}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {workshop.title}
              </h1>
              {workshop.description && (
                <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                  {workshop.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-4 mt-4">
                {workshop.difficulty_level && (
                  <StatusBadge
                    label={
                      workshop.difficulty_level.charAt(0).toUpperCase() +
                      workshop.difficulty_level.slice(1)
                    }
                    variant={
                      difficultyVariants[workshop.difficulty_level] || "neutral"
                    }
                  />
                )}
                {workshop.estimated_minutes && (
                  <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <ClockIcon className="w-4 h-4" />
                    <span>{formatDuration(workshop.estimated_minutes)}</span>
                  </div>
                )}
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {sortedExercises.length} exercise
                  {sortedExercises.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Exercise List */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 sticky top-8">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Exercises
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedExerciseIndex + 1} of {sortedExercises.length}
                </p>
              </div>
              <div className="p-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                {sortedExercises.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    <p className="text-sm">
                      No exercises in this workshop yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sortedExercises.map((exercise, index) => (
                      <button
                        key={exercise.id}
                        onClick={() => setSelectedExerciseIndex(index)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          index === selectedExerciseIndex
                            ? "bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                            : "hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {exercise.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {getContentTypeIcon(exercise.content_type)}
                              <span className="capitalize">
                                {exercise.content_type}
                              </span>
                              {exercise.estimated_minutes && (
                                <>
                                  <span>-</span>
                                  <span>{exercise.estimated_minutes} min</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {sortedExercises.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Exercises Yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  This workshop doesn&apos;t have any exercises. Add exercises
                  from the admin console.
                </p>
                <Button onClick={() => navigate("/admin")}>
                  Go to Admin Console
                </Button>
              </div>
            ) : (
              <>
                {/* Exercise Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold">
                        {selectedExerciseIndex + 1}
                      </span>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedExercise?.title}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      {getContentTypeIcon(selectedExercise?.content_type || "")}
                      <span className="capitalize">
                        {selectedExercise?.content_type}
                      </span>
                    </div>
                  </div>
                  {selectedExercise?.description && (
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedExercise.description}
                    </p>
                  )}
                </div>

                {/* Exercise Content */}
                {selectedExercise && renderExerciseContent(selectedExercise)}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8">
                  <button
                    onClick={goToPrevExercise}
                    disabled={selectedExerciseIndex === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Exercise {selectedExerciseIndex + 1} of{" "}
                    {sortedExercises.length}
                  </span>
                  <button
                    onClick={goToNextExercise}
                    disabled={
                      selectedExerciseIndex === sortedExercises.length - 1
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
